import { Button } from "@design-system/Button/Button";
import React, { useCallback, useState } from "react";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { ManualJournalRecurringUpdate } from "@common/types/domains/accounting/manualJournalRecurringRule";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import styles from "./ConfirmRecurringModal.module.scss";

interface Props {
    show?: boolean;
    onCancel: () => void;
    onSubmit: (
        recurringRuleUpdateType: ManualJournalRecurringUpdate | null,
    ) => void;
    title: string;
    subtitle: string;
    loading?: boolean;
}

const UPDATE_TYPES = [
    { value: ManualJournalRecurringUpdate.CURRENT, label: "Only this" },
    {
        value: ManualJournalRecurringUpdate.FOLLOWING,
        label: "This and following",
    },
    { value: ManualJournalRecurringUpdate.ALL, label: "All" },
];

export const ConfirmRecurringModal: React.FC<Props> = ({
    show,
    onCancel,
    onSubmit,
    title,
    subtitle,
    loading,
}) => {
    const [recurringRuleUpdateType, setRecurringRuleUpdateType] =
        useState<ManualJournalRecurringUpdate | null>(null);
    const handleChange = useCallback(
        (value: ManualJournalRecurringUpdate) => {
            setRecurringRuleUpdateType(value);
        },
        [setRecurringRuleUpdateType],
    );
    return (
        <PlainModal show={show}>
            <StandardModalHeader>{title}</StandardModalHeader>

            <div className={styles.text}>{subtitle}</div>

            <RadioGroup<ManualJournalRecurringUpdate | "">
                name="recurringRuleUpdateType"
                aria-label={title}
                value={recurringRuleUpdateType ?? ""}
                onValueChange={(value) => {
                    if (value !== "") {
                        handleChange(value);
                    }
                }}
                className={styles.radioContainer}
            >
                {UPDATE_TYPES.map(({ value, label }) => (
                    <div key={label} className={styles.radio}>
                        <Radio value={value} label={label} />
                    </div>
                ))}
            </RadioGroup>

            <div className={styles.footer}>
                <Button
                    size="md"
                    variant="secondary"
                    type="button"
                    onClick={() => {
                        onCancel();
                    }}
                    disabled={loading ?? false}
                >
                    Cancel
                </Button>
                <ButtonWithLoader
                    size="md"
                    variant="primary"
                    loading={loading ?? false}
                    disabled={!recurringRuleUpdateType}
                    onClick={() => {
                        onSubmit(recurringRuleUpdateType);
                    }}
                >
                    Save
                </ButtonWithLoader>
            </div>
        </PlainModal>
    );
};
